<script lang="ts">
  import * as _ from "lodash";
  import type { SellSheet } from "@shared/types/SellSheet";
  import SellSheetDetail from "./SellSheetDetail.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import SelectFilter from "@shared/svelte/Components/SelectFilter.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";

  export let sellSheets: SellSheet[];
  export let filters: { [key: string]: any };
  export let loadingState: boolean;

  const { trackClick } = EventTracking;

  const filterSellSheets = async () => {
    loadingState = true;
    let response = await HttpPromise({
      method: "GET",
      url: "/api/retailer/sell_sheets",
      data: filters,
    });
    sellSheets = response;
    loadingState = false;
  };
  let distributorsList = sellSheets.map(sellSheet => sellSheet.distributor);
  let defaultDistro: { id: number; name?: string }[] = [{ id: null, name: "All Distributors" }];
  let distributors = distributorsList
    .reduce((arr, item) => {
      if (!arr.some(obj => obj.id === item.id)) {
        arr.push({ ...item, name: item.retailer_facing_name });
      }
      return arr;
    }, defaultDistro)
    .sort((a, b) => (a.name > b.name ? 1 : -1));

  const runSearch = _.debounce(filterSellSheets, 250);

  $: emptyState = filters.search?.length
    ? emptyStateText["search"]
    : emptyStateText["noSellsheets"];
  let emptyStateText = {
    search: {
      title: "Nothing matches your search",
      text: "No rep recommendations match your search criteria, please change your criteria to view rep recommendations",
    },
    noSellsheets: {
      title: "You don't have any Rep Recommendations",
      text: `<a href="/settings/accounts">Connect your distributors</a> so they can curate products for you`,
    },
  };

</script>

<style lang="scss">
  .inner-section-continer {
    @media (min-width: 1300px) {
      max-width: 1350px;
      margin-left: auto;
      margin-right: auto;
    }
  }

</style>

<div class="fizz-styles">
  <section class="section-continer fizz-surface">
    <section class="fizz-border-bottom ">
      <section class="inner-section-continer">
        <header class="fizz-surface fizz-inset-square-24 fizz-stack-8">
          <div class="header-title">
            <h1 class="fizz-heading-3 fizz-stack-8">Rep Recommendations</h1>
            <p>
              These product sheets are prepared for you by your sales reps. Add recommended products
              to your cart directly from each sheet.
            </p>
          </div>
        </header>
      </section>
    </section>
    <section class="inner-section-continer fizz-inset-square-24">
      <div class="fizz-flex-row-8-responsive fizz-stack-24">
        <input
          type="text"
          id="sell-sheet-search"
          data-testId="sell-sheets-search-bar"
          class="fizz-input-search fizz-full-width"
          name="sell-sheet-search"
          placeholder="Search by name, sender or distributor"
          bind:value={filters.search}
          on:keyup={runSearch}
          on:focus={() => trackClick("sell_sheet_index_search_bar_click")}
        />
        <SelectFilter
          fontColor={"#262626"}
          selectId={"sell-sheets-distributor"}
          testId={"sell-sheets-distributor-filter"}
          options={distributors}
          bind:selectedValue={filters.distributor_id}
          onChange={filterSellSheets}
          onFocus={() => trackClick("sell_sheet_index_distributor_filter_click")}
        />
        <DatePicker
          bind:startDate={filters.start_date}
          bind:endDate={filters.end_date}
          defaultButtonLabel={"All Dates"}
          testId={"sell-sheets-date-filter"}
          runFilters={filterSellSheets}
          minWidth={"275px"}
          maxDate={new Date()}
          selectButtonStyle={true}
          onFocus={() => trackClick("sell_sheet_index_date_filter_click")}
        />
      </div>
      <div>
        {#if sellSheets.length}
          {#if loadingState}
            <div class="fizz-center-content fizz-inset-squish-96" style="height: 20rem;">
              <div class="fizz-icon-spinner fizz-stack-64" />
            </div>
          {:else}
            {#each sellSheets as sellSheet (sellSheet.id)}
              <SellSheetDetail {sellSheet} />
            {/each}
          {/if}
        {:else if loadingState}
          <div class="fizz-center-content fizz-inset-squish-96" style="height: 20rem;">
            <div class="fizz-icon-spinner fizz-stack-64" />
          </div>
        {:else}
          <div class="fizz-inset-squish-64 fizz-text-center" style="height: 40rem;">
            <p class="fizz-text-strong fizz-stack-24">{emptyState.title}</p>
            <p>{@html emptyState.text}</p>
          </div>
        {/if}
      </div>
    </section>
  </section>
</div>
